// src/pages/IncomeAnalysis.tsx
import React, { useEffect, useState } from "react";
import {
    Box, Typography, MenuItem, Select,
    Dialog, Divider, IconButton, useMediaQuery,
    LinearProgress, Button, Container,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
    FileDownloadOutlined, StarOutlined, InfoOutlined,
    TrendingUpOutlined, NorthEastOutlined, CloseOutlined,
} from "@mui/icons-material";
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

/* ─── Brand ──────────────────────────────────────────────────────── */
const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    red:     '#FF4D6A',
};

const CATEGORY_META: Record<string, { color: string; emoji: string; tip: string }> = {
    Salary:      { color: B.green,   emoji: "💼", tip: "Your primary income stream. Consistency here builds a strong credit foundation." },
    Freelance:   { color: "#007AFF", emoji: "💻", tip: "Great supplemental income. Consistent invoicing helps with financial tracking." },
    Investments: { color: B.light,   emoji: "📈", tip: "Passive income is powerful. Reinvesting dividends compounds wealth over time." },
    Gifts:       { color: "#FF9500", emoji: "🎁", tip: "Non-recurring. Avoid factoring gifts into your monthly budget baseline." },
};

const rawDataByBank: any = {
    all: {
        2024: {
            August: [
                { name: "Salary",      value: 450000, transactions: [{ date: "2024-08-01", description: "Monthly Salary",         amount: 450000, implication: "Regular income from employer" }] },
                { name: "Freelance",   value: 180000, transactions: [{ date: "2024-08-12", description: "Website project payment", amount: 180000, implication: "Freelance web design job" }] },
                { name: "Investments", value: 60000,  transactions: [{ date: "2024-08-25", description: "Stock dividends",         amount: 60000,  implication: "Quarterly dividend income" }] },
                { name: "Gifts",       value: 25000,  transactions: [{ date: "2024-08-18", description: "Birthday gift",           amount: 25000,  implication: "Gift from family" }] },
            ],
            September: [
                { name: "Salary",      value: 460000, transactions: [{ date: "2024-09-01", description: "Monthly Salary",         amount: 460000, implication: "Regular income from employer" }] },
                { name: "Freelance",   value: 220000, transactions: [{ date: "2024-09-10", description: "App UI Design Project",   amount: 220000, implication: "Freelance work" }] },
                { name: "Investments", value: 50000,  transactions: [{ date: "2024-09-27", description: "Mutual fund returns",     amount: 50000,  implication: "Investment income" }] },
                { name: "Gifts",       value: 30000,  transactions: [{ date: "2024-09-15", description: "Anniversary gift",        amount: 30000,  implication: "Gift from friend" }] },
            ],
        },
    },
};

const monthsOrder = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const DarkTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ background: B.dark, border: `1px solid ${alpha(B.light, .2)}`, borderRadius: 2, px: 1.5, py: 1, boxShadow: `0 8px 24px ${alpha(B.deep, .6)}` }}>
            <Typography sx={{ color: alpha('#fff', .4), fontSize: '.6rem', letterSpacing: '.08em', mb: .25 }}>{payload[0].payload.month || payload[0].name}</Typography>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.88rem', fontVariantNumeric: 'tabular-nums' }}>₦{Number(payload[0].value).toLocaleString()}</Typography>
        </Box>
    );
};

const IncomeAnalysis: React.FC = () => {
    const navigate  = useNavigate();
    const location  = useLocation();
    const theme     = useTheme();
    const isMobile  = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark    = theme.palette.mode === "dark";

    const { bank, year, month } = location.state || { bank: "all", year: "2024", month: "September" };

    const [selectedBank,     setSelectedBank]     = useState(bank);
    const [selectedYear,     setSelectedYear]     = useState(year);
    const [selectedMonth,    setSelectedMonth]    = useState(month);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [displayedTotal,   setDisplayedTotal]   = useState(0);

    const data            = rawDataByBank[selectedBank]?.[selectedYear]?.[selectedMonth] || [];
    const totalIncome     = data.reduce((sum: number, e: any) => sum + e.value, 0);
    const categoryDetails = data.find((d: any) => d.name === selectedCategory);

    const currentMonthIndex = monthsOrder.indexOf(selectedMonth);
    const prevMonth = monthsOrder[currentMonthIndex - 1];
    const prevData  = rawDataByBank[selectedBank]?.[selectedYear]?.[prevMonth] || [];

    const getChangePercent = (name: string, current: number) => {
        const prev = prevData.find((d: any) => d.name === name)?.value || 0;
        return prev === 0 ? null : ((current - prev) / prev) * 100;
    };

    useEffect(() => {
        if (!totalIncome) return;
        let start = 0;
        const increment = totalIncome / (500 / 16);
        const timer = setInterval(() => {
            start += increment;
            if (start >= totalIncome) { setDisplayedTotal(totalIncome); clearInterval(timer); }
            else setDisplayedTotal(Math.round(start));
        }, 16);
        return () => clearInterval(timer);
    }, [totalIncome, selectedMonth, selectedYear, selectedBank]);

    const handleYearChange = (e: any) => {
        const y = e.target.value;
        setSelectedYear(y);
        const months = Object.keys(rawDataByBank[selectedBank]?.[y] || {});
        if (months.length) setSelectedMonth(months[0]);
    };

    const trendData = monthsOrder
        .filter(m => rawDataByBank[selectedBank]?.[selectedYear]?.[m])
        .map(m => ({
            month: m.slice(0, 3),
            income: (rawDataByBank[selectedBank]?.[selectedYear]?.[m] || []).reduce((s: number, d: any) => s + d.value, 0),
        }));

    const pieData = data.map((d: any) => ({
        name: d.name, value: d.value,
        color: CATEGORY_META[d.name]?.color || B.purple,
    }));

    const topEarner = [...data].sort((a: any, b: any) => b.value - a.value)[0];

    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;

    const filterSx = {
        color: '#fff',
        background: alpha('#fff', .1),
        borderRadius: 2,
        fontSize: '.78rem',
        fontFamily: "'DM Sans', system-ui, sans-serif",
        '.MuiOutlinedInput-notchedOutline': { borderColor: alpha('#fff', .2) },
        '.MuiSvgIcon-root': { color: alpha('#fff', .6) },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#fff', .4) },
    };

    return (
        <Box sx={{ minHeight: "100vh", background: isDark ? B.deep : B.lavender, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO HEADER ════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-56px', sm: 0 },
                pt: { xs: 'calc(56px + 44px)', sm: '44px' },
                pb: 14, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.green, .14)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -20, left: -40, width: 180, height: 180, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                            <ArrowBackIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                        <Box>
                            <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>Analysis</Typography>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>Income Breakdown</Typography>
                        </Box>
                    </Box>
                    <IconButton sx={{ color: alpha('#fff', .55), background: alpha('#fff', .08), border: `1px solid ${alpha('#fff', .1)}`, width: 36, height: 36 }}>
                        <FileDownloadOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                </Box>

                <Box display="flex" gap={1.5}>
                    <Select size="small" value={selectedYear} onChange={handleYearChange} sx={{ ...filterSx, minWidth: 100 }}>
                        {Object.keys(rawDataByBank[selectedBank] || {}).map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </Select>
                    <Select size="small" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} sx={{ ...filterSx, minWidth: 130 }}>
                        {Object.keys(rawDataByBank[selectedBank]?.[selectedYear] || {}).map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </Select>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: -8, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Hero summary ── */}
                <Box sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', background: `linear-gradient(145deg, ${B.dark}, ${B.mid})`, boxShadow: `0 12px 40px ${alpha(B.deep, .45)}` }}>
                    <Box sx={{ p: 2.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                                <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: .75 }}>
                                    {selectedMonth}, {selectedYear}
                                </Typography>
                                <Typography sx={{ color: '#fff', fontFamily: "'Instrument Serif', serif", fontSize: '2.6rem', fontWeight: 400, lineHeight: 1, letterSpacing: '-.02em', mb: .4 }}>
                                    ₦{displayedTotal.toLocaleString()}
                                </Typography>
                                <Typography sx={{ color: alpha('#fff', .35), fontSize: '.72rem', mb: 1.5 }}>Total Income</Typography>
                                {topEarner && (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .75, background: alpha(B.green, .12), border: `1px solid ${alpha(B.green, .25)}`, borderRadius: 8, px: 1.25, py: .5 }}>
                                        <StarOutlined sx={{ fontSize: 12, color: B.green }} />
                                        <Typography sx={{ color: B.green, fontSize: '.63rem', fontWeight: 700, letterSpacing: '.04em' }}>
                                            TOP: {topEarner.name.toUpperCase()} · ₦{topEarner.value.toLocaleString()}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            {pieData.length > 0 && (
                                <Box sx={{ width: 80, height: 80, flexShrink: 0 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={pieData} cx="50%" cy="50%" innerRadius={24} outerRadius={38} dataKey="value" strokeWidth={0}>
                                                {pieData.map((d: any, i: number) => <Cell key={i} fill={d.color} />)}
                                            </Pie>
                                            <Tooltip content={<DarkTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ px: 2.5, py: 1.5, background: alpha('#000', .2), display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {pieData.map((d: any) => (
                            <Box key={d.name} display="flex" alignItems="center" gap={.6}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: d.color }} />
                                <Typography sx={{ fontSize: '.63rem', color: alpha('#fff', .45), fontWeight: 600 }}>{d.name}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Trend chart ── */}
                {trendData.length > 0 && (
                    <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, p: 2.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Income Trend</Typography>
                                <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .35), mt: .2 }}>{selectedYear}</Typography>
                            </Box>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .5, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .2)}`, borderRadius: 8, px: 1, py: .4 }}>
                                <NorthEastOutlined sx={{ fontSize: 11, color: B.green }} />
                                <Typography sx={{ color: B.green, fontSize: '.62rem', fontWeight: 700 }}>GROWING</Typography>
                            </Box>
                        </Box>
                        <ResponsiveContainer width="100%" height={140}>
                            <BarChart data={trendData} barSize={28} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                                <CartesianGrid strokeDasharray="4 4" stroke={alpha(isDark ? '#fff' : '#000', .05)} vertical={false} />
                                <XAxis dataKey="month" tick={{ fontSize: 11, fill: alpha(isDark ? '#fff' : B.ink, .35) as string }} axisLine={false} tickLine={false} />
                                <YAxis tickFormatter={v => `₦${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: alpha(isDark ? '#fff' : B.ink, .28) as string }} axisLine={false} tickLine={false} width={48} />
                                <Tooltip content={<DarkTooltip />} />
                                <defs>
                                    <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={B.green} stopOpacity={.9} />
                                        <stop offset="100%" stopColor={B.green} stopOpacity={.25} />
                                    </linearGradient>
                                </defs>
                                <Bar dataKey="income" radius={[4,4,0,0]} fill="url(#incomeGrad)" name="Income" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                )}

                {/* ── Category cards ── */}
                <Typography sx={{ fontWeight: 700, fontSize: '.63rem', color: alpha(isDark ? '#fff' : B.ink, .3), letterSpacing: '.16em', textTransform: 'uppercase', mb: 1.5 }}>Sources</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1.5, mb: 2 }}>
                    {data.map((cat: any) => {
                        const meta    = CATEGORY_META[cat.name] || { color: B.purple, emoji: "💰", tip: "" };
                        const percent = ((cat.value / (totalIncome || 1)) * 100).toFixed(1);
                        const change  = getChangePercent(cat.name, cat.value);
                        return (
                            <Box key={cat.name} onClick={() => setSelectedCategory(cat.name)} sx={{
                                borderRadius: 3, p: 2.25, cursor: 'pointer',
                                background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
                                transition: 'all .2s',
                                '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 32px ${alpha(meta.color, .18)}`, borderColor: alpha(meta.color, .25) },
                            }}>
                                <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                                    <Box display="flex" alignItems="center" gap={1.25}>
                                        <Box sx={{ width: 38, height: 38, borderRadius: 2.5, background: alpha(meta.color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.95rem', flexShrink: 0 }}>
                                            {meta.emoji}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '.82rem', letterSpacing: '-.015em', color: isDark ? '#fff' : B.ink }}>{cat.name}</Typography>
                                            <Typography sx={{ color: meta.color, fontWeight: 700, fontSize: '.95rem', fontFamily: "'Instrument Serif', serif" }}>
                                                ₦{cat.value.toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {change !== null && (
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .2, background: alpha(change > 0 ? B.green : B.red, .1), borderRadius: 8, px: .9, py: .35, flexShrink: 0 }}>
                                            {change > 0 ? <ArrowDropUpIcon sx={{ color: B.green, fontSize: 14 }} /> : <ArrowDropDownIcon sx={{ color: B.red, fontSize: 14 }} />}
                                            <Typography sx={{ color: change > 0 ? B.green : B.red, fontSize: '.62rem', fontWeight: 700 }}>{Math.abs(change).toFixed(1)}%</Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ height: 4, borderRadius: 4, background: alpha(isDark ? '#fff' : B.dark, .07), overflow: 'hidden', mb: .75 }}>
                                    <Box sx={{ width: `${percent}%`, height: '100%', borderRadius: 4, background: `linear-gradient(90deg, ${B.purple}, ${meta.color})`, transition: 'width .8s cubic-bezier(.16,1,.3,1)' }} />
                                </Box>
                                <Typography sx={{ fontSize: '.63rem', color: alpha(isDark ? '#fff' : B.ink, .3), fontWeight: 600 }}>{percent}% of total</Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Insights ── */}
                {data.length > 0 && (
                    <Box sx={{ borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>
                        <Box sx={{ px: 2.5, pt: 2.25, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}` }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: 2, background: alpha(B.green, .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <InfoOutlined sx={{ color: B.green, fontSize: 16 }} />
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Income Insights</Typography>
                        </Box>
                        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                            {data.map((cat: any) => {
                                const meta = CATEGORY_META[cat.name];
                                return meta ? (
                                    <Box key={cat.name} display="flex" gap={1.5} alignItems="flex-start">
                                        <Box sx={{ width: 28, height: 28, borderRadius: 2, background: alpha(meta.color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.8rem', flexShrink: 0 }}>
                                            {meta.emoji}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: '.78rem', fontWeight: 600, color: isDark ? '#fff' : B.ink, mb: .2 }}>{cat.name}</Typography>
                                            <Typography sx={{ fontSize: '.74rem', color: alpha(isDark ? '#fff' : B.ink, .45), lineHeight: 1.6 }}>{meta.tip}</Typography>
                                        </Box>
                                    </Box>
                                ) : null;
                            })}
                        </Box>
                    </Box>
                )}
            </Container>

            {/* ── Dialog ── */}
            <Dialog open={!!selectedCategory} onClose={() => setSelectedCategory(null)} fullWidth maxWidth="sm"
                    PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', background: isDark ? B.dark : '#fff', backgroundImage: 'none' } }}>
                {categoryDetails && (() => {
                    const meta = CATEGORY_META[selectedCategory!] || { color: B.green, emoji: "💰", tip: "" };
                    return (
                        <>
                            <Box sx={{ background: `linear-gradient(145deg, ${B.deep}, ${B.mid})`, px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: alpha(meta.color, .15), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                                        {meta.emoji}
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: '-.02em' }}>{selectedCategory}</Typography>
                                        <Typography sx={{ color: alpha('#fff', .4), fontSize: '.7rem' }}>₦{categoryDetails.value.toLocaleString()} this month</Typography>
                                    </Box>
                                </Box>
                                <IconButton size="small" onClick={() => setSelectedCategory(null)} sx={{ color: alpha('#fff', .4), '&:hover': { color: '#fff' } }}>
                                    <CloseOutlined sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>
                            <Box sx={{ px: 2.5, py: 1.75, background: alpha(meta.color, .05), borderBottom: `1px solid ${alpha(meta.color, .1)}` }}>
                                <Typography sx={{ fontSize: '.76rem', color: alpha(isDark ? '#fff' : B.ink, .5), lineHeight: 1.6, fontStyle: 'italic' }}>{meta.tip}</Typography>
                            </Box>
                            {categoryDetails.transactions.map((tx: any, idx: number, arr: any[]) => (
                                <Box key={idx}>
                                    <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                        <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha(meta.color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <TrendingUpOutlined sx={{ color: meta.color, fontSize: 17 }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                                <Typography sx={{ fontWeight: 600, fontSize: '.85rem', color: isDark ? '#fff' : B.ink }}>{tx.description}</Typography>
                                                <Typography sx={{ fontWeight: 700, color: B.green, fontSize: '.88rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>+₦{tx.amount.toLocaleString()}</Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .35), mt: .25 }}>{tx.date}</Typography>
                                            <Box sx={{ mt: 1, p: 1.25, background: alpha(meta.color, .06), borderRadius: 2, borderLeft: `2.5px solid ${meta.color}` }}>
                                                <Typography sx={{ fontSize: '.74rem', color: alpha(isDark ? '#fff' : B.ink, .5), lineHeight: 1.5 }}>{tx.implication}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    {idx < arr.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .07) }} />}
                                </Box>
                            ))}
                            <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
                                <Button fullWidth onClick={() => setSelectedCategory(null)} sx={{
                                    background: alpha(B.purple, .08), color: B.purple, borderRadius: 2.5,
                                    textTransform: 'none', fontWeight: 700, fontSize: '.8rem', py: 1.2,
                                    border: `1px solid ${alpha(B.purple, .2)}`,
                                    '&:hover': { background: alpha(B.purple, .15) },
                                }}>
                                    Close
                                </Button>
                            </Box>
                        </>
                    );
                })()}
            </Dialog>
        </Box>
    );
};

export default IncomeAnalysis;